import React from 'react';
import { useTranslation } from 'react-i18next';
import { HeartIcon, UserGroupIcon, ShieldCheckIcon, LightBulbIcon, StarIcon, ClockIcon, ScaleIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const AboutPage = () => {
  const { t, i18n } = useTranslation();

  const values = [
    {
      icon: HeartIcon,
      title: t('aboutPage.values.respect.title'),
      description: t('aboutPage.values.respect.description')
    },
    {
      icon: LightBulbIcon,
      title: t('aboutPage.values.innovation.title'),
      description: t('aboutPage.values.innovation.description')
    },
    {
      icon: ShieldCheckIcon,
      title: t('aboutPage.values.security.title'),
      description: t('aboutPage.values.security.description')
    },
    {
      icon: UserGroupIcon,
      title: t('aboutPage.values.community.title'),
      description: t('aboutPage.values.community.description')
    },
    {
      icon: ScaleIcon,
      title: t('aboutPage.values.equality.title'),
      description: t('aboutPage.values.equality.description')
    }
  ];

  const features = [
    {
      title: t('aboutPage.features.smartQR.title'),
      description: t('aboutPage.features.smartQR.description')
    },
    {
      title: t('aboutPage.features.multilingual.title'),
      description: t('aboutPage.features.multilingual.description')
    },
    {
      title: t('aboutPage.features.quality.title'),
      description: t('aboutPage.features.quality.description')
    },
    {
      title: t('aboutPage.features.lifetime.title'),
      description: t('aboutPage.features.lifetime.description')
    },
    {
      title: t('aboutPage.features.support.title'),
      description: t('aboutPage.features.support.description')
    },
    {
      title: t('aboutPage.features.gallery.title'),
      description: t('aboutPage.features.gallery.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <SEO page="about" />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-600/90 to-orange-700/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center">
            <p className="text-xl sm:text-2xl font-light text-amber-100 mb-4">
              ✨ {t('aboutPage.motto.main')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('aboutPage.hero.title')}
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl font-light text-amber-100 mb-8 max-w-4xl mx-auto">
              {t('aboutPage.hero.subtitle')}
            </p>
            <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 leading-relaxed">
              {t('aboutPage.hero.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                {t('aboutPage.mission.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('aboutPage.mission.paragraph1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('aboutPage.mission.paragraph2')}
              </p>
            </div>
            <div className="mt-8 lg:mt-0">
              <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-2xl p-8 border border-amber-200">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full mb-6 mx-auto">
                  <HeartIcon className="w-8 h-8 text-white" />
                </div>
                <blockquote className="text-xl font-medium text-gray-900 text-center leading-relaxed">
                  "{t('aboutPage.mission.quote')}"
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('aboutPage.values.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-amber-100">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl mb-4">
                  <value.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('aboutPage.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('aboutPage.features.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 hover:shadow-lg transition-all duration-300">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full">
                    <StarIcon className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            {t('aboutPage.pricing.title')}
          </h2>
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            {t('aboutPage.pricing.description')}
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('aboutPage.pricing.includes')}
          </p>
          <div className="mt-8 inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-orange-600 text-white px-8 py-4 rounded-2xl shadow-lg">
            <span className="text-4xl font-bold">1990 МКД</span>
            <span className="ml-3 text-lg">/ {t('demo.pricing.lifetime')}</span>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 lg:py-24 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-amber-400 mb-2">2025</div>
              <div className="text-sm text-gray-300">Година на основање</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-amber-400 mb-2">24/7</div>
              <div className="text-sm text-gray-300">Достапност на платформата</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-amber-400 mb-2">7</div>
              <div className="text-sm text-gray-300">Поддржани јазици</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-amber-400 mb-2">∞</div>
              <div className="text-sm text-gray-300">Доживотен пристап</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 lg:py-24 bg-gradient-to-r from-amber-600 to-orange-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {t('aboutPage.cta.title')}
          </h2>
          <p className="text-xl text-amber-100 mb-8">
            {t('aboutPage.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/mk/products"
              className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-xl text-amber-700 bg-white hover:bg-amber-50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              {t('aboutPage.cta.orderPlate')}
            </a>
            <a
              href="/mk/contact"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-base font-semibold rounded-xl text-white hover:bg-white hover:text-amber-600 transition-all duration-300 hover:scale-105"
            >
              {t('nav.contact')}
            </a>
            <a
              href={`/${i18n.language}/faq`}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-base font-semibold rounded-xl text-white hover:bg-white hover:text-amber-600 transition-all duration-300 hover:scale-105"
            >
              {t('nav.faq')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
